import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { I18nProvider } from "@/i18n/I18nContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { MemberAuthProvider } from "@/contexts/MemberAuthContext";

import PublicLayout from "@/components/layout/PublicLayout";
import Home from "@/pages/Home";
import Documents from "@/pages/Documents";
import Galerie from "@/pages/Galerie";
import Events from "@/pages/Events";
import Agenda from "@/pages/Agenda";
import Dormir from "@/pages/Dormir";
import EventDetail from "@/pages/EventDetail";
import Association from "@/pages/Association";
import Histoire from "@/pages/Histoire";
import SoutiensInstitutionnels from "@/pages/SoutiensInstitutionnels";
import FetesInternationalesIndex from "@/pages/FetesInternationalesIndex";
import FetesInternationalesDetail from "@/pages/FetesInternationalesDetail";
import FetesPopulaires2027 from "@/pages/FetesPopulaires2027";
import Mecenat from "@/pages/Mecenat";
import Contact from "@/pages/Contact";
import Access from "@/pages/Access";
import Bergerie from "@/pages/Bergerie";
import Sejour from "@/pages/Sejour";
import Volunteer from "@/pages/Volunteer";
import BellArticle from "@/pages/BellArticle";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Presence from "@/pages/Presence";
import PasseurDetail from "@/pages/PasseurDetail";
import RGPD from "@/pages/RGPD";
import {
  FetesOrigine, FetesProgramme, FetesPreInscription,
  FetesRencontresJeunes, FetesLiensUtiles, FetesContacts,
  FetesArtistes, FetesPasseurs, FetesPasseursFoi, FetesPasseursHistoireSciences, FetesPlanningDetaille, FetesInscription,
} from "@/pages/fetes/FetesPages";
import CheckIn from "@/pages/CheckIn";
import ScanQR from "@/pages/ScanQR";
import NativeHome from "@/pages/NativeHome";
import Programs from "@/pages/Programs";
import ProgramDetail from "@/pages/ProgramDetail";
import BayCrossing from "@/pages/BayCrossing";
import VivaArtist from "@/pages/VivaArtist";
import { isNative } from "@/lib/native";
import NewsletterUnsubscribe from "@/pages/NewsletterUnsubscribe";
import { MentionsLegales, PolitiqueConfidentialite } from "@/pages/LegalPages";
import {
  EspaceLogin, EspaceRegister, EspaceInvitation, EspaceDashboard, EspaceProfil,
  EspaceMotDePasseOublie, EspaceReinitialiserMotDePasse,
} from "@/pages/espace/EspacePages";
import {
  EspaceForum, EspaceForumTopic, EspaceCovoiturage, EspaceGalerie, EspaceDisponibilites,
} from "@/pages/espace/EspaceV2Pages";
import {
  EspaceHebergements, EspaceAnnuaire, EspaceMotDePasse,
} from "@/pages/espace/EspaceV3Pages";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import Connexion from "@/pages/Connexion";
import Inscription from "@/pages/Inscription";
import InstallPrompt from "@/components/InstallPrompt";
import CookieBanner from "@/components/CookieBanner";
import LanguageDetectionBanner from "@/components/LanguageDetectionBanner";
import NativeBootstrap from "@/components/NativeBootstrap";

import "@/App.css";

function ProtectedAdmin({ children }) {
  const { admin } = useAuth();
  if (admin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center font-body" style={{ color: "var(--muted)" }}>
        Chargement…
      </div>
    );
  }
  if (admin === false) return <Navigate to="/connexion" replace />;
  return children;
}

function PublicRoute({ children }) {
  return <PublicLayout>{children}</PublicLayout>;
}

/**
 * HomeRouter — decides between the mobile-first NativeHome (Capacitor shell)
 * and the classic marketing Home page (regular web / PWA). This is what makes
 * PRESENCE feel like an actual companion app once installed, rather than a
 * dressed-up webview.
 */
function HomeRouter() {
  if (isNative()) {
    return <NativeHome />;
  }
  return <PublicLayout><Home /></PublicLayout>;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <MemberAuthProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomeRouter />} />
            {/* Always-classic marketing home — accessible from within the native
                app via the "See full site" footer link. */}
            <Route path="/accueil" element={<PublicRoute><Home /></PublicRoute>} />
            <Route path="/documents" element={<PublicRoute><Documents /></PublicRoute>} />
            <Route path="/galerie" element={<PublicRoute><Galerie /></PublicRoute>} />
            <Route path="/gallery" element={<Navigate to="/galerie" replace />} />
            <Route path="/evenements" element={<PublicRoute><Events /></PublicRoute>} />
            <Route path="/agenda" element={<PublicRoute><Agenda /></PublicRoute>} />
            <Route path="/dormir-baie-mont-saint-michel" element={<PublicRoute><Dormir /></PublicRoute>} />
            <Route path="/hebergement" element={<Navigate to="/dormir-baie-mont-saint-michel" replace />} />
            <Route path="/offices" element={<PublicRoute><Events category="office" /></PublicRoute>} />
            <Route path="/concerts" element={<PublicRoute><Events category="concert" /></PublicRoute>} />
            <Route path="/conferences" element={<PublicRoute><Events category="conference" /></PublicRoute>} />
            {/* /autres removed on 2026-02-19 — category retired from the public nav */}
            <Route path="/evenements/:id" element={<PublicRoute><EventDetail /></PublicRoute>} />
            <Route path="/association" element={<PublicRoute><Association /></PublicRoute>} />
            <Route path="/histoire" element={<PublicRoute><Histoire /></PublicRoute>} />
            <Route path="/soutiens-institutionnels" element={<PublicRoute><SoutiensInstitutionnels /></PublicRoute>} />
            <Route path="/fetes-internationales" element={<PublicRoute><FetesInternationalesIndex /></PublicRoute>} />
            <Route path="/fetes-internationales/:slug" element={<PublicRoute><FetesInternationalesDetail /></PublicRoute>} />
            <Route path="/fetes-populaires-2027" element={<PublicRoute><FetesPopulaires2027 /></PublicRoute>} />
            <Route path="/fetes-2027" element={<Navigate to="/fetes-populaires-2027" replace />} />
            <Route path="/soutiens" element={<PublicRoute><SoutiensInstitutionnels /></PublicRoute>} />
            <Route path="/partenaires-institutionnels" element={<PublicRoute><SoutiensInstitutionnels /></PublicRoute>} />
            <Route path="/mecenat" element={<PublicRoute><Mecenat /></PublicRoute>} />
            <Route path="/mecenat/merci" element={<PublicRoute><PaymentSuccess /></PublicRoute>} />
            <Route path="/contact" element={<PublicRoute><Contact /></PublicRoute>} />
            <Route path="/acces" element={<PublicRoute><Access /></PublicRoute>} />
            <Route path="/access" element={<Navigate to="/acces" replace />} />
            <Route path="/bergerie" element={<PublicRoute><Bergerie /></PublicRoute>} />
            <Route path="/bergerie-pelerine" element={<Navigate to="/bergerie" replace />} />
            <Route path="/sejour" element={<PublicRoute><Sejour /></PublicRoute>} />
            <Route path="/sejourner" element={<Navigate to="/sejour" replace />} />
            <Route path="/devenir-benevole" element={<PublicRoute><Volunteer /></PublicRoute>} />
            <Route path="/become-a-volunteer" element={<PublicRoute><Volunteer /></PublicRoute>} />
            <Route path="/presence" element={<PublicRoute><Presence /></PublicRoute>} />
            <Route path="/presence/origine" element={<PublicRoute><FetesOrigine /></PublicRoute>} />
            <Route path="/presence/programme" element={<PublicRoute><FetesProgramme /></PublicRoute>} />
            <Route path="/presence/pre-inscription" element={<PublicRoute><FetesPreInscription /></PublicRoute>} />
            <Route path="/presence/inscription" element={<PublicRoute><FetesInscription /></PublicRoute>} />
            <Route path="/presence/planning-detaille" element={<PublicRoute><FetesPlanningDetaille /></PublicRoute>} />
            <Route path="/presence/artistes" element={<PublicRoute><FetesArtistes /></PublicRoute>} />
            <Route path="/presence/passeurs" element={<PublicRoute><FetesPasseurs /></PublicRoute>} />
            <Route path="/presence/passeurs-foi" element={<PublicRoute><FetesPasseursFoi /></PublicRoute>} />
            <Route path="/presence/passeurs-histoire-sciences" element={<PublicRoute><FetesPasseursHistoireSciences /></PublicRoute>} />
            <Route path="/passeurs/:slug" element={<PublicRoute><PasseurDetail /></PublicRoute>} />
            <Route path="/rgpd" element={<PublicRoute><RGPD /></PublicRoute>} />
            <Route path="/privacy" element={<PublicRoute><RGPD /></PublicRoute>} />
            <Route path="/presence/rencontres-jeunes" element={<PublicRoute><FetesRencontresJeunes /></PublicRoute>} />
            <Route path="/presence/liens-utiles" element={<PublicRoute><FetesLiensUtiles /></PublicRoute>} />
            <Route path="/presence/contacts" element={<PublicRoute><FetesContacts /></PublicRoute>} />

            {/* ESPACE MEMBRE */}
            <Route path="/espace" element={<PublicRoute><EspaceDashboard /></PublicRoute>} />
            <Route path="/espace/connexion" element={<PublicRoute><EspaceLogin /></PublicRoute>} />
            <Route path="/espace/inscription" element={<PublicRoute><EspaceRegister /></PublicRoute>} />
            <Route path="/espace/invitation" element={<PublicRoute><EspaceInvitation /></PublicRoute>} />
            <Route path="/espace/mot-de-passe-oublie" element={<PublicRoute><EspaceMotDePasseOublie /></PublicRoute>} />
            <Route path="/espace/reinitialiser-mot-de-passe" element={<PublicRoute><EspaceReinitialiserMotDePasse /></PublicRoute>} />
            <Route path="/espace/profil" element={<PublicRoute><EspaceProfil /></PublicRoute>} />
            <Route path="/espace/forum" element={<PublicRoute><EspaceForum /></PublicRoute>} />
            <Route path="/espace/forum/:id" element={<PublicRoute><EspaceForumTopic /></PublicRoute>} />
            <Route path="/espace/covoiturage" element={<PublicRoute><EspaceCovoiturage /></PublicRoute>} />
            <Route path="/espace/galerie" element={<PublicRoute><EspaceGalerie /></PublicRoute>} />
            <Route path="/espace/disponibilites" element={<PublicRoute><EspaceDisponibilites /></PublicRoute>} />
            <Route path="/espace/hebergements" element={<PublicRoute><EspaceHebergements /></PublicRoute>} />
            <Route path="/espace/annuaire/:role" element={<PublicRoute><EspaceAnnuaire /></PublicRoute>} />
            <Route path="/espace/mot-de-passe" element={<PublicRoute><EspaceMotDePasse /></PublicRoute>} />
            <Route path="/checkin/:token" element={<CheckIn />} />
            <Route path="/scan" element={<ScanQR />} />
            {/* QA / Apple reviewer preview — always renders the native home
                even in a regular browser. Hidden from menus. */}
            <Route path="/native-preview" element={<NativeHome />} />

            {/* Programs — symposiums + stages/résidences (unified system) */}
            <Route path="/symposiums" element={<PublicRoute><Programs kind="symposium" /></PublicRoute>} />
            <Route path="/symposiums/:slug" element={<PublicRoute><ProgramDetail /></PublicRoute>} />
            <Route path="/stages" element={<PublicRoute><Programs kind="stage" /></PublicRoute>} />
            <Route path="/stages/:slug" element={<PublicRoute><ProgramDetail /></PublicRoute>} />

            {/* Traversée de la baie — simple registration form */}
            <Route path="/traversee-de-la-baie" element={<PublicRoute><BayCrossing /></PublicRoute>} />
            {/* VIVA ARTIST — label de production tous médias */}
            <Route path="/viva-artist" element={<PublicRoute><VivaArtist /></PublicRoute>} />
            <Route path="/bapteme-cloche" element={<PublicRoute><BellArticle /></PublicRoute>} />
            <Route path="/paiement-succes" element={<PublicRoute><PaymentSuccess /></PublicRoute>} />
            <Route path="/newsletter/desinscription" element={<PublicRoute><NewsletterUnsubscribe /></PublicRoute>} />
            <Route path="/mentions-legales" element={<PublicRoute><MentionsLegales /></PublicRoute>} />
            <Route path="/confidentialite" element={<PublicRoute><PolitiqueConfidentialite /></PublicRoute>} />

            <Route path="/admin/login" element={<Navigate to="/connexion" replace />} />
            <Route path="/admin" element={<Navigate to="/apleo" replace />} />
            <Route path="/presence/inscription/:token/decline" element={<PublicRoute><Inscription /></PublicRoute>} />
            <Route path="/presence/inscription/:token" element={<PublicRoute><Inscription /></PublicRoute>} />
            <Route path="/connexion" element={<PublicRoute><Connexion /></PublicRoute>} />
            <Route path="/login" element={<Navigate to="/connexion" replace />} />
            <Route path="/espace/login" element={<Navigate to="/connexion" replace />} />
            <Route path="/apleo" element={<ProtectedAdmin><AdminDashboard /></ProtectedAdmin>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <InstallPrompt />
          <CookieBanner />
          <LanguageDetectionBanner />
          <NativeBootstrap />
        </BrowserRouter>
        </MemberAuthProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
